/**
 * useGlassBoxEvents — Glass Box observability event ring buffer (Spec 167)
 *
 * Always-on: events flow for ALL nodes without explicit enable/disable.
 * Per-stream in-memory Map + IndexedDB persistence:
 *   - Stream switch: events preserved in Map, restored instantly on return
 *   - Page refresh: events restored from IndexedDB on mount
 *   - Ring buffer: 500 events per stream, FIFO eviction
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useUserWebSocket } from '../contexts/UserWebSocketContext';
import { loadGlassBoxCache, saveGlassBoxCache, clearGlassBoxCache } from '../lib/glassBoxCache';
import { auth } from '../lib/firebase';
import { eventOwnTimeMs } from '../lib/glassBoxCutoff';
import type { ObservabilityEvent } from '../types/api';

const RING_BUFFER_MAX = 500;
const STORAGE_KEY = 'glass-box-open';
const CACHE_FLUSH_INTERVAL_MS = 2000; // Debounced IndexedDB write interval

export type GlassBoxFilter = 'all' | 'llm' | 'tools' | 'errors';

export interface GlassBoxItem {
  id: string;
  streamId: string;
  receivedAt: number;
  event: string;
  data: Record<string, unknown>;
}

export interface UseGlassBoxEventsResult {
  isOpen: boolean;
  toggle: () => void;
  events: GlassBoxItem[];
  filter: GlassBoxFilter;
  setFilter: (f: GlassBoxFilter) => void;
  filteredEvents: GlassBoxItem[];
  clear: () => void;
  /** Events filtered to a specific node (for Glass Box display) */
  eventsForNode: (nodeId: string) => GlassBoxItem[];
  /** Load events older than `before` (ISO8601) from Axiom for a node (Spec 167 Phase 4) */
  loadEarlierEvents: (nodeId: string, before: string) => Promise<void>;
  /** Whether full history has been loaded for a node */
  hasFullHistory: (nodeId: string) => boolean;
  /** Whether a backfill is currently loading */
  isLoadingHistory: boolean;
}

function classifyEvent(eventName: string): 'llm' | 'tools' | 'errors' | 'other' {
  if (eventName.includes('error')) return 'errors';
  if (eventName.includes('llm')) return 'llm';
  if (eventName.includes('tool')) return 'tools';
  return 'other';
}

function nextId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? `gbe-${crypto.randomUUID()}`
    : `gbe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Cap an array to RING_BUFFER_MAX, keeping the latest entries */
function capBuffer(items: GlassBoxItem[]): GlassBoxItem[] {
  return items.length > RING_BUFFER_MAX
    ? items.slice(items.length - RING_BUFFER_MAX)
    : items;
}

export function useGlassBoxEvents(streamId: string | null, apiBaseUrl?: string): UseGlassBoxEventsResult {
  const { userWebSocket } = useUserWebSocket();

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Per-stream in-memory event cache. Survives stream switches.
  // Key: streamId, Value: GlassBoxItem[]. LRU eviction at MAX_CACHED_STREAMS.
  const MAX_CACHED_STREAMS = 10;
  const cacheRef = useRef<Map<string, GlassBoxItem[]>>(new Map());
  const cacheAccessOrderRef = useRef<string[]>([]); // Most recent last

  // Current stream's events (derived from cacheRef for React rendering)
  const [events, setEvents] = useState<GlassBoxItem[]>([]);
  const [filter, setFilter] = useState<GlassBoxFilter>('all');

  /** Touch a stream in the LRU access order and evict if over limit */
  const touchCache = (sid: string) => {
    const order = cacheAccessOrderRef.current;
    const idx = order.indexOf(sid);
    if (idx >= 0) order.splice(idx, 1);
    order.push(sid);
    // Evict oldest if over limit
    while (order.length > MAX_CACHED_STREAMS) {
      const evicted = order.shift()!;
      cacheRef.current.delete(evicted);
    }
  };

  // Track dirty streams that need IndexedDB flush
  const dirtyStreamsRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Generation counter to invalidate stale IndexedDB hydration after clear()
  const hydrationGenRef = useRef(0);
  // Events fetched by "Load earlier events" for the CURRENT stream, by node (at most 500
  // each, the route's cap). Session-only: kept out of cacheRef so they are never persisted
  // and never push live events out of the ring buffer. Reset with fullHistoryNodes
  // (stream change), so a refresh or switch offers the button again.
  const backfillRef = useRef<Map<string, GlassBoxItem[]>>(new Map());

  /** Live events plus the current stream's backfilled rows, oldest first */
  const withBackfill = (live: GlassBoxItem[]): GlassBoxItem[] => {
    if (backfillRef.current.size === 0) return live;
    return [...Array.from(backfillRef.current.values()).flat(), ...live]
      .sort((a, b) => a.receivedAt - b.receivedAt);
  };

  // Persist open/close state to localStorage (display only)
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
      // ignore
    }
  }, [isOpen]);

  // Flush dirty streams to IndexedDB (debounced)
  const flushToIndexedDB = useCallback(() => {
    const dirty = dirtyStreamsRef.current;
    if (dirty.size === 0) return;
    const cache = cacheRef.current;
    const toFlush = new Set(dirty);
    dirty.clear();
    for (const sid of toFlush) {
      const items = cache.get(sid);
      if (items && items.length > 0) {
        saveGlassBoxCache(sid, items).catch(() => {});
      }
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return; // Already scheduled
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      flushToIndexedDB();
    }, CACHE_FLUSH_INTERVAL_MS);
  }, [flushToIndexedDB]);

  // On stream switch: restore from in-memory cache (instant) or IndexedDB (async)
  const prevStreamIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (streamId === prevStreamIdRef.current) return;

    // Flush previous stream to IndexedDB before switching
    if (prevStreamIdRef.current) {
      flushToIndexedDB();
    }
    prevStreamIdRef.current = streamId;
    backfillRef.current.clear(); // Session-only rows belong to the previous stream

    if (!streamId) {
      setEvents([]);
      return;
    }

    // Check in-memory cache first (instant)
    const cached = cacheRef.current.get(streamId);
    if (cached && cached.length > 0) {
      setEvents(cached);
      return;
    }

    // Fall back to IndexedDB (async).
    // Merge with any live events that arrived between the stream switch and
    // the async IndexedDB read resolving (prevents clobbering newer events).
    setEvents([]); // Show empty while loading
    const loadGen = hydrationGenRef.current; // Capture generation before async read
    loadGlassBoxCache(streamId).then(dbEvents => {
      // Skip if generation changed (clear() was called) or stream switched
      if (loadGen !== hydrationGenRef.current) return;
      if (!dbEvents || dbEvents.length === 0 || prevStreamIdRef.current !== streamId) return;

      const existing = cacheRef.current.get(streamId) ?? [];
      if (existing.length > 0) {
        // Live events arrived before IndexedDB resolved — merge, don't overwrite
        const roundToSec = (ms: number) => Math.floor(ms / 1000);
        const liveKeys = new Set(existing.map(e => `${e.event}:${roundToSec(e.receivedAt)}`));
        const newFromDb = dbEvents.filter(
          e => !liveKeys.has(`${e.event}:${roundToSec(e.receivedAt)}`)
        );
        const merged = capBuffer([...newFromDb, ...existing].sort((a, b) => a.receivedAt - b.receivedAt));
        cacheRef.current.set(streamId, merged); touchCache(streamId);
        setEvents(withBackfill(merged));
      } else {
        // No live events yet — use IndexedDB data as-is
        cacheRef.current.set(streamId, dbEvents); touchCache(streamId);
        setEvents(withBackfill(dbEvents));
      }
    }).catch(() => {});
  }, [streamId, flushToIndexedDB]);

  // Always-on WebSocket event listener
  useEffect(() => {
    if (!userWebSocket || !streamId) return;

    const handler = (sid: string, event: ObservabilityEvent) => {
      if (sid !== streamId) return;
      const item: GlassBoxItem = {
        id: nextId(),
        streamId: sid,
        receivedAt: Date.now(),
        event: event.event,
        data: event.data,
      };

      // Update in-memory cache
      const cache = cacheRef.current;
      const existing = cache.get(sid) ?? [];
      const updated = capBuffer([...existing, item]);
      cache.set(sid, updated); touchCache(sid);

      // Mark dirty for IndexedDB flush
      dirtyStreamsRef.current.add(sid);
      scheduleFlush();

      // Update React state
      setEvents(withBackfill(updated));
    };

    userWebSocket.on('observabilityEvent', handler);
    return () => {
      userWebSocket.off('observabilityEvent', handler);
    };
  }, [userWebSocket, streamId, scheduleFlush]);

  // Flush to IndexedDB on unmount
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
      }
      // Synchronous flush of all dirty streams
      const dirty = dirtyStreamsRef.current;
      const cache = cacheRef.current;
      for (const sid of dirty) {
        const items = cache.get(sid);
        if (items && items.length > 0) {
          saveGlassBoxCache(sid, items).catch(() => {});
        }
      }
      dirty.clear();
    };
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const clear = useCallback(() => {
    hydrationGenRef.current += 1; // Invalidate any in-flight IndexedDB load
    if (streamId) {
      cacheRef.current.delete(streamId);
      backfillRef.current.clear();
      dirtyStreamsRef.current.delete(streamId);
      clearGlassBoxCache(streamId).catch(() => {});
    }
    setEvents([]);
  }, [streamId]);

  const eventsForNode = useCallback((nodeId: string) => {
    return events.filter(e => e.data?.node_id === nodeId);
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter(e => classifyEvent(e.event) === filter);
  }, [events, filter]);

  // Axiom backfill (Phase 4)
  const [fullHistoryNodes, setFullHistoryNodes] = useState<Set<string>>(new Set());
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Reset backfill state on stream switch
  useEffect(() => {
    setFullHistoryNodes(new Set());
  }, [streamId]);

  const hasFullHistory = useCallback((nodeId: string) => {
    return fullHistoryNodes.has(nodeId);
  }, [fullHistoryNodes]);

  const loadEarlierEvents = useCallback(async (nodeId: string, before: string) => {
    if (!streamId || fullHistoryNodes.has(nodeId)) return;
    const capturedStreamId = streamId; // Capture before async to detect stream switch
    setIsLoadingHistory(true);
    try {
      const base = apiBaseUrl ?? import.meta.env.VITE_API_URL ?? '';
      const url = `${base}/observability/streams/${encodeURIComponent(capturedStreamId)}/nodes/${encodeURIComponent(nodeId)}/events?before=${encodeURIComponent(before)}`;
      const token = await auth?.currentUser?.getIdToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(url, { credentials: 'include', headers });
      if (!response.ok) return;

      const data = await response.json() as {
        events: Array<{ event: string; data: Record<string, unknown>; _time: string }>;
        truncated: boolean;
      };

      const backfillItems: GlassBoxItem[] = data.events.map(e => ({
        id: nextId(),
        streamId: streamId!,
        // Own time when the event carries one: `_time` is Axiom's ingest time, later than
        // the event, which would stop dedup matching the same event already received live.
        receivedAt: eventOwnTimeMs(e.data, new Date(e._time).getTime()),
        event: e.event,
        data: e.data,
      }));

      // Verify stream hasn't changed during async fetch
      if (prevStreamIdRef.current !== capturedStreamId) return;

      if (backfillItems.length > 0) {
        const live = cacheRef.current.get(capturedStreamId) ?? [];
        const roundToSec = (ms: number) => Math.floor(ms / 1000);
        const existingKeys = new Set(
          withBackfill(live).map(e => `${e.event}:${roundToSec(e.receivedAt)}`)
        );
        const deduped = backfillItems
          .filter(e => !existingKeys.has(`${e.event}:${roundToSec(e.receivedAt)}`))
          .slice(-RING_BUFFER_MAX);
        // Backfilled rows stay out of cacheRef: not persisted, no live eviction.
        backfillRef.current.set(nodeId, deduped);
        setEvents(withBackfill(live));
      }

      // One load per node (Spec 167): a truncated page is the 500 nearest the cutoff.
      setFullHistoryNodes(prev => new Set([...prev, nodeId]));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [streamId, fullHistoryNodes, apiBaseUrl]);

  return {
    isOpen, toggle, events, filter, setFilter, filteredEvents, clear,
    eventsForNode, loadEarlierEvents, hasFullHistory, isLoadingHistory,
  };
}
